import React, { useMemo, useState } from "react";
import { Button, Collapse, Input, Space, Typography, message } from "antd";
import { CopyOutlined, SnippetsOutlined } from "@ant-design/icons";
import type { RoadmapItem, RoadmapSubtask } from "../../types/roadmap";
import {
  ROADMAP_ASSISTANT_SCHEMA,
  applyAssistantPastePayload,
  buildAssistantBundleMarkdown,
  extractAssistantPasteJson,
  parseAssistantPastePayload,
} from "../../services/roadmapAssistantSync";
import { buildCursorJsonReplyPrompt } from "../../services/roadmapAssistantCursorPrompt";

const { Text } = Typography;
const { TextArea } = Input;

interface RoadmapAssistantSyncPanelProps {
  item: RoadmapItem;
  subtasks: RoadmapSubtask[];
  onRefresh: () => Promise<void>;
  /** When false, exports and paste ignore working notes (same as official unlock). */
  officialUnlocked: boolean;
}

export const RoadmapAssistantSyncPanel: React.FC<RoadmapAssistantSyncPanelProps> = ({
  item,
  subtasks,
  onRefresh,
  officialUnlocked,
}) => {
  const [paste, setPaste] = useState("");
  const [loading, setLoading] = useState(false);

  const companyQuestions = useMemo(() => {
    const list = item.roadmap_company_questions ?? [];
    return [...list].sort((a, b) => a.sort_order - b.sort_order);
  }, [item.roadmap_company_questions]);

  const copyBundle = async () => {
    const md = buildAssistantBundleMarkdown(item, subtasks, companyQuestions, {
      includeWorkingNotes: officialUnlocked,
    });
    try {
      await navigator.clipboard.writeText(md);
      message.success("Copied bundle for assistant");
    } catch {
      message.error("Clipboard failed");
    }
  };

  const copyCursorReplyPrompt = async () => {
    const text = buildCursorJsonReplyPrompt(item.id, {
      includeWorkingNotesInPaste: officialUnlocked,
    });
    try {
      await navigator.clipboard.writeText(text);
      message.success("Copied instructions for Cursor");
    } catch {
      message.error("Clipboard failed");
    }
  };

  const apply = async () => {
    const raw = extractAssistantPasteJson(paste);
    if (raw === null) {
      message.error(
        "No valid JSON found. Use a ```json ... ``` fenced block or paste raw JSON only."
      );
      return;
    }
    const parsed = parseAssistantPastePayload(raw, item.id);
    if (!parsed.ok) {
      message.error(parsed.error);
      return;
    }
    const payload = { ...parsed.payload };
    if (!officialUnlocked) {
      delete payload.workingNotes;
      const hasOther =
        payload.expectedFinishAt !== undefined ||
        (payload.removeSubtaskIds && payload.removeSubtaskIds.length > 0) ||
        (payload.subtasks && payload.subtasks.length > 0) ||
        (payload.removeCompanyQuestionIds && payload.removeCompanyQuestionIds.length > 0) ||
        (payload.companyQuestions && payload.companyQuestions.length > 0);
      if (!hasOther) {
        message.error(
          "Unlock official edit to apply working notes, or include expected finish, subtasks, or company questions in the paste."
        );
        return;
      }
    }
    setLoading(true);
    try {
      const result = await applyAssistantPastePayload(payload, onRefresh);
      if (!result.ok) {
        message.error(result.error);
      } else {
        message.success("Applied assistant update");
        setPaste("");
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <Collapse
      size="small"
      bordered={false}
      style={{
        marginBottom: 10,
        background: "#fafafa",
        borderRadius: 8,
        border: "1px solid #f0f0f0",
      }}
      defaultActiveKey={[]}
      items={[
        {
          key: "assistant",
          label: (
            <Text strong style={{ fontSize: 13 }}>
              Assistant round-trip (copy / paste)
            </Text>
          ),
          children: (
            <div>
              <Space wrap size="small">
                <Button type="default" size="small" icon={<CopyOutlined />} onClick={() => void copyBundle()}>
                  Copy task bundle
                </Button>
                <Button type="default" size="small" icon={<SnippetsOutlined />} onClick={() => void copyCursorReplyPrompt()}>
                  Copy Cursor reply prompt
                </Button>
              </Space>
              <Text type="secondary" style={{ display: "block", marginTop: 6, fontSize: 12, lineHeight: 1.45 }}>
                Paste the <strong>task bundle</strong> into Cursor first. Then paste the <strong>reply prompt</strong> so
                the model returns one <code>json</code> code block you can copy straight into{" "}
                <strong>Apply assistant paste</strong> below.
              </Text>
              <Collapse
                size="small"
                style={{ marginTop: 8, background: "transparent" }}
                defaultActiveKey={[]}
                items={[
                  {
                    key: "paste",
                    label: "Apply assistant paste",
                    children: (
                      <>
                        <TextArea
                          rows={8}
                          value={paste}
                          onChange={(e) => setPaste(e.target.value)}
                          placeholder={`Paste assistant reply (must include JSON with "schema": "${ROADMAP_ASSISTANT_SCHEMA}" and matching itemId)`}
                          style={{ fontFamily: "monospace", fontSize: 12 }}
                        />
                        <Button
                          type="primary"
                          size="small"
                          loading={loading}
                          onClick={() => void apply()}
                          style={{ marginTop: 8 }}
                        >
                          Apply assistant paste
                        </Button>
                      </>
                    ),
                  },
                ]}
              />
            </div>
          ),
        },
      ]}
    />
  );
};
